import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import {
  Loader2, RefreshCw, Search, ShieldAlert, AlertTriangle, Info, XCircle, CheckCircle2,
  ShoppingCart, CreditCard, MessageCircle, Bell, Sparkles, Server, HardDrive, Truck, X, Circle,
} from 'lucide-react';

const SEV = {
  critical: { label: 'Critical', chip: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500', icon: XCircle },
  high: { label: 'High', chip: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500', icon: ShieldAlert },
  warning: { label: 'Warning', chip: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', icon: AlertTriangle },
  info: { label: 'Info', chip: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500', icon: Info },
};
const CAT_ICON = {
  ORDER: ShoppingCart, PAYMENT: CreditCard, WHATSAPP: MessageCircle, NOTIFICATION: Bell,
  AI: Sparkles, API: Server, MEDIA: HardDrive, SHIPPING: Truck,
};
const STATUS = {
  open: { label: 'Open', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  investigating: { label: 'Investigating', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  resolved: { label: 'Resolved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};
const DATE_PRESETS = [
  { key: '', label: 'All time' }, { key: 'today', label: 'Today' }, { key: '24h', label: 'Last 24 hours' },
  { key: '7d', label: 'Last 7 days' }, { key: '30d', label: 'Last 30 days' },
];

function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' +
      d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function SevChip({ s }) {
  const cfg = SEV[s] || SEV.info; const Icon = cfg.icon;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${cfg.chip}`}><Icon className="w-3 h-3" />{cfg.label}</span>;
}

export default function AdminErrorCentre() {
  const [data, setData] = useState({ items: [], total: 0, summary: {}, categories: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [filters, setFilters] = useState({ date_preset: '', category: '', severity: '', status: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selected, setSelected] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);

  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 350); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async (isRefresh) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (debouncedQ) params.q = debouncedQ;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/admin/errors', { params });
      setData(res.data);
    } catch (e) { /* handled by empty state */ }
    finally { setLoading(false); setRefreshing(false); }
  }, [page, pageSize, debouncedQ, filters]);

  useEffect(() => { load(false); }, [load]);
  // Auto-refresh every 60s
  useEffect(() => { const id = setInterval(() => load(true), 60000); return () => clearInterval(id); }, [load]);

  const openDetail = async (id) => {
    try { const res = await api.get(`/admin/errors/${id}`); setSelected(res.data); }
    catch { /* ignore */ }
  };

  const changeStatus = async (st) => {
    if (!selected) return;
    setStatusSaving(true);
    try {
      await api.post(`/admin/errors/${selected.id}/status`, { status: st });
      setSelected({ ...selected, status: st });
      load(true);
    } catch { /* ignore */ } finally { setStatusSaving(false); }
  };

  const s = data.summary || {};
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));
  const allClear = (s.total || 0) === 0;

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="admin-error-centre">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-fab-text" data-testid="errors-title">Error / Issue Centre</h1>
          <p className="text-[13px] text-fab-text-secondary mt-1">Monitor and investigate application failures across the FABTEXS AI platform.</p>
        </div>
        <button onClick={() => load(true)} data-testid="errors-refresh"
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-medium text-fab-text hover:bg-fab-elevated transition-colors">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Summary */}
      <div className={`rounded-xl border p-4 mb-5 flex flex-wrap items-center gap-x-8 gap-y-3 ${allClear ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-fab-primary-lighter'}`} data-testid="errors-summary">
        <div className="flex items-center gap-2">
          {allClear ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <ShieldAlert className="w-5 h-5 text-orange-500" />}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">Platform Errors</div>
            <div className="text-[15px] font-bold text-fab-text" data-testid="errors-summary-label">
              {allClear ? 'All Systems Clear' : `${s.total} Issue${s.total === 1 ? '' : 's'} Recorded`}
            </div>
          </div>
        </div>
        {!allClear && (
          <div className="flex items-center gap-6 text-[13px]">
            <div><span className="font-bold text-rose-600">{s.critical || 0}</span> <span className="text-fab-text-muted">Critical</span></div>
            <div><span className="font-bold text-orange-600">{s.high || 0}</span> <span className="text-fab-text-muted">High</span></div>
            <div><span className="font-bold text-amber-600">{s.warning || 0}</span> <span className="text-fab-text-muted">Warning</span></div>
            <div><span className="font-bold text-fab-primary">{s.last_24h || 0}</span> <span className="text-fab-text-muted">Last 24h</span></div>
            <div><span className="font-bold text-fab-text">{s.open || 0}</span> <span className="text-fab-text-muted">Open</span></div>
          </div>
        )}
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fab-text-muted" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="errors-search"
            placeholder="Search error, ID, seller, order, endpoint, category..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] focus:outline-none focus:ring-2 focus:ring-fab-primary/30" />
        </div>
        <button onClick={() => setShowFilters(v => !v)} data-testid="errors-filters-btn"
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-medium text-fab-text hover:bg-fab-elevated">
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="bg-white border border-fab-primary-lighter rounded-xl p-4 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="errors-filter-panel">
          <Select label="Date" testid="errors-filter-date" value={filters.date_preset} onChange={(v) => { setFilters({ ...filters, date_preset: v }); setPage(1); }}
            options={DATE_PRESETS.map(d => ({ value: d.key, label: d.label }))} />
          <Select label="Category" testid="errors-filter-category" value={filters.category} onChange={(v) => { setFilters({ ...filters, category: v }); setPage(1); }}
            options={[{ value: '', label: 'All categories' }, ...(data.categories || []).map(c => ({ value: c.key, label: c.label }))]} />
          <Select label="Severity" testid="errors-filter-severity" value={filters.severity} onChange={(v) => { setFilters({ ...filters, severity: v }); setPage(1); }}
            options={[{ value: '', label: 'All' }, { value: 'critical', label: 'Critical' }, { value: 'high', label: 'High' }, { value: 'warning', label: 'Warning' }, { value: 'info', label: 'Info' }]} />
          <Select label="Status" testid="errors-filter-status" value={filters.status} onChange={(v) => { setFilters({ ...filters, status: v }); setPage(1); }}
            options={[{ value: '', label: 'All' }, { value: 'open', label: 'Open' }, { value: 'investigating', label: 'Investigating' }, { value: 'resolved', label: 'Resolved' }]} />
          <div className="sm:col-span-4">
            <button onClick={() => { setFilters({ date_preset: '', category: '', severity: '', status: '' }); setPage(1); }}
              data-testid="errors-filter-clear" className="text-[12.5px] text-fab-primary font-medium hover:underline">Clear filters</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-fab-primary-lighter rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-20 flex items-center justify-center text-fab-text-muted"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : data.items.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center text-center px-6" data-testid="errors-empty">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-3"><CheckCircle2 className="w-7 h-7 text-emerald-500" /></div>
            <div className="text-[15px] font-semibold text-fab-text">No issues detected</div>
            <div className="text-[13px] text-fab-text-secondary mt-1">No application errors have been recorded for the selected period.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-fab-text-muted border-b border-fab-primary-lighter">
                  <th className="px-4 py-3 font-semibold">Date &amp; Time</th>
                  <th className="px-3 py-3 font-semibold">Severity</th>
                  <th className="px-3 py-3 font-semibold">Category</th>
                  <th className="px-3 py-3 font-semibold">Error</th>
                  <th className="px-3 py-3 font-semibold">Seller</th>
                  <th className="px-3 py-3 font-semibold">Buyer</th>
                  <th className="px-3 py-3 font-semibold">Order ID</th>
                  <th className="px-3 py-3 font-semibold">Status</th>
                  <th className="px-3 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => {
                  const CatIcon = CAT_ICON[r.category] || Server;
                  const st = STATUS[r.status] || STATUS.open;
                  return (
                    <tr key={r.id} className="border-b border-fab-primary-lighter/60 hover:bg-fab-elevated/50 cursor-pointer" onClick={() => openDetail(r.id)} data-testid={`error-row-${r.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-fab-text-secondary">{fmtWhen(r.timestamp)}{r.count > 1 && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-fab-elevated text-fab-text-muted">×{r.count}</span>}</td>
                      <td className="px-3 py-3"><SevChip s={r.severity} /></td>
                      <td className="px-3 py-3 whitespace-nowrap"><span className="inline-flex items-center gap-1.5 text-fab-text"><CatIcon className="w-4 h-4 text-fab-text-muted" />{r.category_label}</span></td>
                      <td className="px-3 py-3 max-w-[280px]"><span className="block truncate text-fab-text" title={r.message}>{r.message}</span></td>
                      <td className="px-3 py-3 whitespace-nowrap text-fab-text-secondary">{r.seller || <span className="text-fab-text-muted">Not available</span>}</td>
                      <td className="px-3 py-3 whitespace-nowrap text-fab-text-secondary">{r.buyer || <span className="text-fab-text-muted">Not available</span>}</td>
                      <td className="px-3 py-3 whitespace-nowrap font-mono text-[12px]">{r.order_id || <span className="text-fab-text-muted">Not available</span>}</td>
                      <td className="px-3 py-3"><span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${st.cls}`}>{st.label}</span></td>
                      <td className="px-3 py-3"><button onClick={(e) => { e.stopPropagation(); openDetail(r.id); }} className="text-[12.5px] text-fab-primary font-medium hover:underline" data-testid={`error-view-${r.id}`}>View</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-[12.5px]" data-testid="errors-pager">
          <div className="text-fab-text-muted">
            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, data.total)} of {data.total} issues
          </div>
          <div className="flex items-center gap-2">
            <select value={pageSize} onChange={(e) => { setPageSize(+e.target.value); setPage(1); }} data-testid="errors-page-size"
              className="rounded-lg border border-fab-primary-lighter px-2 py-1.5 bg-white">
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="errors-prev"
              className="px-3 py-1.5 rounded-lg border border-fab-primary-lighter disabled:opacity-40 hover:bg-fab-elevated">Prev</button>
            <span className="text-fab-text-muted">Page {page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="errors-next"
              className="px-3 py-1.5 rounded-lg border border-fab-primary-lighter disabled:opacity-40 hover:bg-fab-elevated">Next</button>
          </div>
        </div>
      )}

      <div className="text-center text-[11.5px] text-fab-text-muted mt-6">
        Error capture runs server-side on genuine failures. Page auto-refresh: 60s.
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="error-detail-drawer">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-fab-primary-lighter px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2"><SevChip s={selected.severity} /><span className="text-[13px] font-semibold text-fab-text">{selected.category_label}</span></div>
              <button onClick={() => setSelected(null)} data-testid="error-detail-close" className="text-fab-text-muted hover:text-fab-text"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-5">
              <Section title="Error Information">
                <Row k="Error ID" v={<span className="font-mono text-[11.5px]">{selected.id}</span>} />
                <Row k="Timestamp" v={fmtWhen(selected.timestamp)} />
                <Row k="Category" v={selected.category_label} />
                <Row k="Severity" v={<SevChip s={selected.severity} />} />
                <Row k="Status" v={<span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${(STATUS[selected.status] || STATUS.open).cls}`}>{(STATUS[selected.status] || STATUS.open).label}</span>} />
                <Row k="Occurrences" v={selected.count || 1} />
                <Row k="Message" v={<span className="text-fab-text">{selected.message}</span>} />
              </Section>
              <Section title="Affected Context">
                <Row k="Seller" v={selected.seller || 'Not available'} />
                <Row k="Buyer" v={selected.buyer || 'Not available'} />
                <Row k="Order ID" v={selected.order_id || 'Not available'} />
              </Section>
              <Section title="Technical Information">
                <Row k="Endpoint" v={selected.endpoint ? `${selected.method || ''} ${selected.endpoint}` : 'Not available'} />
                <Row k="HTTP status" v={selected.http_status || 'Not available'} />
                <Row k="Provider" v={selected.provider || 'Not available'} />
                <Row k="IP address" v={selected.ip || 'Not available'} />
                <Row k="Device" v={[selected.browser, selected.os].filter(Boolean).join(' · ') || 'Not available'} />
                <Row k="App version" v={selected.app_version || 'Not available'} />
                <Row k="Build" v={selected.app_build || 'Not available'} />
              </Section>
              <Section title="Diagnostic Details">
                <pre className="text-[11.5px] bg-fab-elevated rounded-lg p-3 whitespace-pre-wrap break-words text-fab-text-secondary">{selected.detail || selected.message || 'No further details.'}</pre>
              </Section>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-2">Update Status</div>
                <div className="flex gap-2">
                  {['open', 'investigating', 'resolved'].map(st => (
                    <button key={st} disabled={statusSaving || selected.status === st} onClick={() => changeStatus(st)}
                      data-testid={`error-status-${st}`}
                      className={`px-3 py-1.5 rounded-lg text-[12.5px] font-medium border transition-colors disabled:opacity-50 ${selected.status === st ? 'bg-fab-primary text-white border-fab-primary' : 'border-fab-primary-lighter text-fab-text hover:bg-fab-elevated'}`}>
                      {STATUS[st].label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-2">{title}</div>
    <dl className="space-y-1.5">{children}</dl>
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex gap-3 text-[13px]"><dt className="w-28 shrink-0 text-fab-text-muted">{k}</dt><dd className="flex-1 text-fab-text break-words">{v}</dd></div>
);
function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full rounded-lg border border-fab-primary-lighter px-2.5 py-2 text-[13px] bg-white">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
